import { Check } from 'lucide-react';
import { Fragment, type Ref, useMemo } from 'react';

import {
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
} from '@apps/web/components/ui/command';
import {
    filterOrganizationMembershipOptions,
    getOrganizationBadgeColor,
    getOrganizationInitial,
    type OrganizationMembershipOption,
} from '@apps/web/lib/organization_display';

type OrganizationMembershipPickerContentProps = {
    readonly options: ReadonlyArray<OrganizationMembershipOption>;
    readonly selectedOrganizationId: string | null;
    readonly searchValue: string;
    readonly onSearchValueChange: (value: string) => void;
    readonly onSelectOrganization: (organizationId: string) => void;
    readonly searchInputRef?: Ref<HTMLInputElement>;
};

export function OrganizationMembershipPickerContent({
    options,
    selectedOrganizationId,
    searchValue,
    onSearchValueChange,
    onSelectOrganization,
    searchInputRef,
}: OrganizationMembershipPickerContentProps) {
    const filteredOptions = useMemo(
        () => filterOrganizationMembershipOptions(options, searchValue),
        [options, searchValue],
    );

    return (
        <Fragment>
            <div className="px-2 py-1.5 text-sm font-medium">
                Select Organization
            </div>
            <Command shouldFilter={false}>
                <CommandInput
                    placeholder="Search organizations"
                    value={searchValue}
                    onValueChange={onSearchValueChange}
                    ref={searchInputRef}
                />
                <CommandList>
                    <CommandEmpty>No organizations found</CommandEmpty>
                    {filteredOptions.map((organization) => {
                        const organizationColor = getOrganizationBadgeColor(
                            organization.organization_id,
                        );
                        const organizationInitial = getOrganizationInitial(
                            organization.organization_name,
                        );

                        return (
                            <CommandItem
                                key={organization.organization_id}
                                value={organization.organization_id}
                                className="flex cursor-pointer items-center gap-2"
                                onSelect={() =>
                                    onSelectOrganization(
                                        organization.organization_id,
                                    )
                                }
                            >
                                <span
                                    className="flex h-[20px] w-[20px] flex-none items-center justify-center rounded-[4px] text-xs font-semibold text-white uppercase"
                                    style={{
                                        backgroundColor:
                                            organizationColor ?? '#475569',
                                    }}
                                >
                                    {organizationInitial}
                                </span>
                                <span className="min-w-0 flex-1 truncate">
                                    {organization.displayName}
                                </span>
                                {organization.organization_id ===
                                selectedOrganizationId ? (
                                    <Check className="h-4 w-4 flex-none" />
                                ) : null}
                            </CommandItem>
                        );
                    })}
                </CommandList>
            </Command>
        </Fragment>
    );
}
